import { PlayerClass } from '@/types/profile'
import {
  AlternatePassiveAddition,
  AlternatePassiveSkill,
  AlternateTreeVersion,
  CharacterDefinition,
  ClusterJewelNotable,
  CostType,
  CraftingBenchOption,
  EssenceDefinition,
  GemDefinition,
  ItemClassDefinition,
  ItemDefinition,
  ItemTemplateDefinition,
  JewelSlotDefinition,
  ModDefinition,
  PantheonDefinition,
  PassiveMasteryDefinition,
  PassiveSkillDefinition,
  PassiveSkillWithRange,
  PassiveTreeData,
  PassiveTreeDefinition,
  PassiveTreeLegacyNode,
  PassiveTreeNode,
  QuantifierDefinition,
  StatDefinition,
  StatSemantic,
  TattooDefinition,
  TranslationDomain,
  UniqueDefinition
} from './types'

export class DataClass {
  readonly orbitRadii = [0, 82, 162, 335, 493, 662, 846]
  readonly skillsPerOrbit = [1, 6, 16, 16, 40, 72, 72]

  getOrbitAngle(size: number, index: number) {
    let degrees
    if (size === 16) {
      degrees = [0, 30, 45, 60, 90, 120, 135, 150, 180, 210, 225, 240, 270, 300, 315, 330][index]
    } else if (size === 40) {
      degrees = [
        0, 10, 20, 30, 40, 45, 50, 60, 70, 80, 90, 100, 110, 120, 130, 135, 140, 150, 160, 170, 180, 190, 200, 210, 220,
        225, 230, 240, 250, 260, 270, 280, 290, 300, 310, 315, 320, 330, 340, 350
      ][index]
    } else {
      degrees = (360 * index) / size
    }
    return (degrees * Math.PI) / 180
  }

  reservationTypes: Record<string, string> = {
    mana_flat: '{0} Mana',
    mana_percent: '{0}% Mana',
    life_flat: '{0} Life',
    life_percent: '{0}% Life'
  }

  jewel_radii!: {
    name: string
    radius: number
    ring_inner: number
    ring_outer: number
  }[]

  characters!: Record<string, CharacterDefinition>
  crafting_bench_options!: CraftingBenchOption[]
  essences!: Record<string, EssenceDefinition>
  gem_tags!: Record<string, string | null>
  gems!: Record<string, GemDefinition>
  icons!: Record<string, string[]>
  item_classes!: Record<string, ItemClassDefinition>
  pantheon!: Record<string, PantheonDefinition>
  items!: Record<string, ItemDefinition>
  item_templates!: Record<string, ItemTemplateDefinition>
  flavour!: Record<string, string>
  mods!: Record<string, ModDefinition>
  quantifiers!: Record<string, QuantifierDefinition>
  semantics!: Record<string, StatSemantic>
  stats!: Record<string, StatDefinition>
  cost_types!: Record<string, CostType>
  reminder_text!: Record<string, string>
  uniques: Record<string, UniqueDefinition> = {}
  translations!: TranslationDomain[]
  versions!: number[]
  defaultVersion!: number
  cluster_jewel_notables!: ClusterJewelNotable[]
  jewel_slots!: Record<string, JewelSlotDefinition>
  jewel_slot_list!: string[]
  affliction_stats!: Record<string, string>

  alternate_passive_skills!: AlternatePassiveSkill[]
  alternate_passive_additions!: AlternatePassiveAddition[]
  alternate_tree_versions!: AlternateTreeVersion[]
  tattoos!: Record<string, TattooDefinition>

  timeless_skills: Record<string, PassiveSkillWithRange> = {}
  trees: { [key in number]?: PassiveTreeData } = {}

  passive_skills!: Record<string, PassiveSkillDefinition>
  passive_mastery!: Record<string, PassiveMasteryDefinition>
  passive_tree!: PassiveTreeDefinition
  atlas_passive_tree!: PassiveTreeDefinition
  atlas_passive_tree_league?: PassiveTreeDefinition

  ascendancyClass: { [key in string]?: PlayerClass } = {}
  iconMap: Record<string, Record<string, number>> = {}

  itemByName: Record<string, ItemDefinition> = {}
  itemByType: Record<string, ItemDefinition[]> = {}
  itemByHash: Record<string, ItemDefinition> = {}

  item_remap?: Record<string, string>
  gem_remap?: Record<string, string>
}

const Data = new DataClass()

export default Data

export function treeData(version?: number) {
  if (!version) version = Data.defaultVersion
  return Data.trees[version]
}

export function treeNode(id: number | string, version?: number, atlas?: boolean, league?: boolean) {
  if (typeof id === 'string' && Data.timeless_skills[id]) {
    return Data.timeless_skills[id]
  }
  const data = treeData(version)
  if (!data) return
  const passive_skills = 'passive_skills' in data ? data.passive_skills : Data.passive_skills
  if (typeof id === 'string' && passive_skills[id]) {
    return passive_skills[id]
  }
  const node = (
    atlas ? (league !== false && data.atlas_passive_tree_league) || data.atlas_passive_tree : data.passive_tree
  ).nodes[Number(id)]
  if (!node) return
  if ('passive_skills' in data) {
    return data.passive_skills[(node as PassiveTreeNode).skill_id]
  } else {
    return node as PassiveTreeLegacyNode
  }
}
